/**
 * Swedish wording for the error messages the backend sends back.
 *
 * Magento has no Swedish language pack, so its own messages ("Not enough items for sale",
 * "Invalid login or password.") arrive in English, as do the storefront packages' own. The
 * shop is Swedish only, so a message is shown either translated here or as the caller's
 * Swedish fallback — never as the English original.
 *
 * Matched on the text, not on an error code: Magento tags most of these `UNDEFINED`, and the
 * login and register messages come from the storefront package with codes of their own.
 */

type Translation = string | ((match: RegExpMatchArray) => string);

const TRANSLATIONS: ReadonlyArray<readonly [RegExp, Translation]> = [
  // Cart and stock
  [/^product (.+) is out of stock\.?$/i, (m) => `${m[1]} är slut i lager.`],
  [/^this product is out of stock\.?$/i, 'Varan är slut i lager.'],
  [/^some of the products are out of stock\.?$/i, 'Några av varorna är slut i lager.'],
  [/^not enough items for sale\.?$/i, 'Det finns inte så många av varan i lager.'],
  [/^the requested qty\.? is not available\.?$/i, 'Det antal du valt finns inte i lager.'],
  [/^the requested qty exceeds the maximum qty allowed in shopping cart\.?$/i, 'Du har valt fler än vad som går att köpa.'],
  [/^the most you may purchase is (\d+)\.?$/i, (m) => `Du kan köpa högst ${m[1]} st.`],
  [/^the fewest you may purchase is (\d+)\.?$/i, (m) => `Du måste köpa minst ${m[1]} st.`],
  [/^product that you are trying to add is not available\.?$/i, 'Varan går inte att köpa just nu.'],
  [/^this product is disabled\.?$/i, 'Varan går inte att köpa just nu.'],
  [/^could not find a product with sku/i, 'Varan finns inte längre.'],
  [/^please specify the quantity for at least one product\.?$/i, 'Välj antal för minst en vara.'],
  [/^(could not find a cart|the cart isn't active|no such entity with cart_?id)/i, 'Kundvagnen har gått ut. Ladda om sidan och försök igen.'],

  // Coupons
  [/^the coupon code isn't valid/i, 'Rabattkoden är inte giltig.'],
  [/^the coupon code couldn't be applied/i, 'Rabattkoden kunde inte användas.'],
  [/^the coupon code couldn't be deleted/i, 'Rabattkoden kunde inte tas bort.'],
  [/^coupon code is required\.?$/i, 'Ange en rabattkod.'],
  [/^too many coupon code requests/i, 'För många försök med rabattkoder. Vänta en stund och försök igen.'],

  // Sign in and account
  [/^(invalid login or password|invalid email or password)\.?$/i, 'Fel e-postadress eller lösenord.'],
  [/^the account sign-in was incorrect/i, 'Inloggningen misslyckades. Kontrollera uppgifterna eller vänta en stund och försök igen.'],
  [/^your account has been locked/i, 'Ditt konto är låst. Kontakta kundtjänst.'],
  [/^please confirm your email address before signing in\.?$/i, 'Bekräfta din e-postadress innan du loggar in.'],
  [/^too many login attempts/i, 'För många inloggningsförsök. Vänta en stund och försök igen.'],
  [/^(a customer with the same email address already exists|an account with this email already exists)/i, 'Det finns redan ett konto med den här e-postadressen.'],
  [/^please choose a stronger password/i, 'Välj ett starkare lösenord, minst 8 tecken med både bokstäver och siffror.'],
  [/^the password doesn't match this account/i, 'Lösenordet stämmer inte.'],
  [/^(please enter a valid email|enter a valid email address|invalid email (address|format))/i, 'Ange en giltig e-postadress.'],
  // Password reset (Magento: token mismatched/expired, or the email does not match the token)
  [/^the password token is (mismatched|expired)/i, 'Återställningslänken är ogiltig eller har gått ut. Begär en ny länk.'],
  [/^(invalid value of .* provided for the token field|cannot set the customer'?s password|the customer account (was )?not found)/i, 'Återställningslänken är ogiltig eller har gått ut. Begär en ny länk.'],
  [/^(not logged in|please log in|the current customer isn't authorized|sorry, guest checkout is not available|guest checkout is (disabled|not available))/i, 'Logga in för att fortsätta.'],
  [/^network error/i, 'Det gick inte att nå butiken. Kontrollera din anslutning och försök igen.'],

  // Checkout
  [/^(the shipping method is missing|please specify a shipping method)/i, 'Välj ett leveranssätt.'],
  [/^(invalid shipping method|this shipping method is not available)/i, 'Leveranssättet går inte att välja.'],
  [/^the requested payment method is not available\.?$/i, 'Betalsättet går inte att välja.'],

  // Licensed checkout (Ecomero_LicensedCheckout), for when they arrive untranslated
  [/^sign in to buy licence-required products\.?$/i, 'Logga in för att köpa licenspliktiga varor.'],
  [/^confirm your licence details before placing the order\.?$/i, 'Bekräfta dina licensuppgifter innan du lägger ordern.'],
  [/^licence-required products cannot be paid with svea\.?$/i, 'Licenspliktiga varor kan inte betalas med Svea.'],
  [/must use the licensed checkout\.?$/i, 'Varukorgen innehåller licenspliktiga varor och måste gå via licenskassan.'],
];

/** Letters only Swedish text has; a message containing one is taken to be Swedish already. */
const SWEDISH_LETTERS = /[åäö]/i;

/** The real text without the adapter's "GraphQL errors:" prefix and a trailing "[request id]". */
export function unwrapBackendMessage(message: string): string {
  return message
    .replace(/^GraphQL errors:\s*/i, '')
    .replace(/\s*\[[^\]]*\]\s*$/, '')
    .trim();
}

const unwrap = unwrapBackendMessage;

/**
 * The Swedish wording of a backend message, or null when it is unknown English (or empty).
 * A message already in Swedish — one an Ecomero module translated — is given back as it is.
 */
export function translateBackendMessage(message: string | null | undefined): string | null {
  const text = unwrap(message ?? '');
  if (!text) return null;

  for (const [pattern, translation] of TRANSLATIONS) {
    const match = text.match(pattern);
    if (!match) continue;
    return typeof translation === 'string' ? translation : translation(match);
  }

  return SWEDISH_LETTERS.test(text) ? text : null;
}

/** A backend message for the shopper: its Swedish wording, or `fallback`. */
export function customerMessage(message: string | null | undefined, fallback: string): string {
  return translateBackendMessage(message) ?? fallback;
}

/** `customerMessage` for a caught error. */
export function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? customerMessage(err.message, fallback) : fallback;
}
